import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Sigma } from "lucide-react";
import { cn } from "@/shared/lib/cn";

export interface DerivedValueProps {
  label: string;
  /** El valor ya formateado. Aquí no se calcula nada ni se divide nada. */
  value: ReactNode;
  /** Una línea corta de contexto bajo el valor. */
  note?: ReactNode;
  /** El renglón que manda del bloque: se pinta más grande. */
  emphasis?: boolean;
  /**
   * Aprieta el relleno, como `FormSection`. Es para cuando el valor
   * calculado va en la MISMA rejilla que los campos que lo alimentan: sin
   * esto mide veinte píxeles más que el `<input>` de al lado y desalinea
   * la fila entera, además de sumar alto en un paso que pelea por caber.
   */
  compact?: boolean;
  className?: string;
}

/**
 * Un valor que NADIE tecleó: lo calcula el sistema a partir de otros
 * campos.
 *
 * Existe para que no se confunda con un campo editable. Un número que
 * sale de una cuenta y se pinta igual que un `<input>` invita a
 * intentar cambiarlo, y cuando no se deja, a pensar que la pantalla
 * está rota. Aquí se marca de tres formas a la vez —el marco sin borde
 * de campo, el icono de sumatoria y el rótulo "Calculado" que también
 * se lee en voz alta— porque el color solo no vale para quien no lo
 * distingue.
 *
 * No sabe de paquetes. El primer resumen de pedido o de comisiones que
 * exista lo va a necesitar igual.
 */
export function DerivedValue({
  label,
  value,
  note,
  emphasis = false,
  compact = false,
  className,
}: DerivedValueProps) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        "rounded-2xl border border-dashed border-ds-border bg-ds-cream-2/50",
        compact ? "px-3 py-1.5" : "px-4 py-3",
        className,
      )}
    >
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-ds-label text-ds-ink-3">
        <Sigma className="h-3.5 w-3.5" aria-hidden="true" />
        {label}
        <span className="sr-only"> — {t("Calculado")}</span>
      </p>
      <p
        className={cn(
          "mt-1 font-semibold tabular-nums text-ds-ink",
          emphasis ? "text-[22px] leading-tight" : "text-[15px]",
        )}
      >
        {value}
      </p>
      {note && (
        <p
          className={cn(
            "mt-1 leading-relaxed text-ds-ink-3",
            compact ? "text-[11px]" : "text-[12px]",
          )}
        >
          {note}
        </p>
      )}
    </div>
  );
}
